import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import type { History } from 'history';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import routePaths from '../../route-paths';
import { Button, CheckIcon, HistoryIcon, Search, Theme } from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { ProfileSite } from './billing-manager-types';
import { BillingProfileCoverageQuery, TabsConnectionAuditQuery } from './billing-manager-queries';
import { VColumn, VRowAction, VTable } from '../../components/v-table';
import { VTableType } from '../../components/v-table/v-table-types';
import moment from 'moment';
import ScrollContext from '../../layouts/scroll-context';

const useStyles = createUseStyles(
  {
    wrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      minHeight: 'calc(100vh - 54px)',
    },
    headerRow: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 15,
    },
    select: {
      flexGrow: 1,
      maxWidth: 400,
    },
    errors: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    error: {
      border: `1px solid ${Theme.red}`,
      background: Theme.red10,
      color: Theme.redDark,
      padding: [4, 10],
    },
    validationErrors: {
      extend: 'errors',
      height: 30,
    },
    errorsInner: {
      height: 30,
      overflow: 'hidden',
      position: 'relative',
      zIndex: 20,
      background: Theme.white,
      paddingBottom: 20,
    },
    view: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    missingProfile: {
      color: Theme.redDark,
    },
    table: {
      border: `1px solid ${Theme.grey30}`,
      position: 'relative',
    },
    refresh: {
      fill: Theme.linkTextColor,
      '&>svg': {
        height: 20,
        width: 20,
      },
    },
    checkIcon: {
      fill: Theme.green,
      width: 30,
      height: 30,
    },
  },
  { name: 'BillingManagerList' },
);

const BillingManagerList: React.FC<{ history: History }> = ({ history }) => {
  const classes = useStyles();

  const [searchText, setSearchText] = useState<string>('');

  const scrollContainer = useContext(ScrollContext);

  const {
    data: sitesData,
    loading: loadingSites,
    refetch: refreshCoverage,
  } = useQuery(BillingProfileCoverageQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: auditData,
    loading: loadingAudit,
    refetch: refreshAudit,
  } = useQuery(TabsConnectionAuditQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const sitesList = useMemo(() => {
    return sitesData?.adminBillingProfileCoverageQuery || [];
  }, [sitesData]);

  useEffect(() => {
    TitleService.setTitles('Billing Manager');
    Breadcrumbs.set([
      {
        url: routePaths.billingManager,
        title: 'Billing Manager',
      },
    ]);
  }, []);

  const { auditErrorMap, errorCount } = useMemo(() => {
    let errorMap = new Map<string, string[]>();
    let count = 0;
    if (auditData) {
      errorMap = auditData?.adminTabsConnectionAuditQuery.reduce((errorMap, entry) => {
        count += entry.errors.length;
        errorMap.set(entry._id, entry.errors);
        return errorMap;
      }, new Map<string, string[]>());
    }
    return {
      auditErrorMap: errorMap,
      errorCount: count,
    };
  }, [auditData?.adminTabsConnectionAuditQuery]);

  const columns = useMemo(() => {
    return [
      new VColumn({
        field: 'siteName',
        displayName: 'Seller',
        sortable: true,
      }),
      new VColumn({
        field: 'billingProfileName',
        displayName: 'TABS Billing Entity',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'asc',
        formatter: (value) => value || <span className={classes.missingProfile}>No Billing Profile!</span>,
      }),
      new VColumn({
        field: 'createdAt',
        displayName: 'Onboarded On',
        sortable: true,
        formatter: (value) => (value ? moment(value).format('MMM D, YYYY') : 'N/A'),
      }),
      new VColumn({
        field: 'billingProfileId',
        displayName: 'Errors',
        sortable: false,
        formatter: (billingProfileId) => {
          const errors = auditErrorMap?.get(billingProfileId as string);
          if (!errors) return '';
          if (errors.length === 0) return <CheckIcon className={classes.checkIcon} />;
          const formattedErrors = errors.join(', ');
          return <div className={classes.error}>{formattedErrors}</div>;
        },
      }),
    ];
  }, [classes, auditErrorMap]);

  const handleRowClick = (row: ProfileSite) => {
    history.push(`${routePaths.billingManager}/org/${row._id}`);
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const handleRefreshAuditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    refreshAudit();
    refreshCoverage();
  };

  const ProfileCoverageTable = VTable as VTableType<ProfileSite>;

  return (
    <div className={classes.wrap}>
      <div className={classes.headerRow}>
        <Search onChange={handleSearch} />
        <div className={classes.validationErrors}>
          Validations:
          {loadingAudit ? (
            <div>Loading...</div>
          ) : (
            <div>
              {errorCount > 0 ? (
                <div className={classes.error}>{errorCount} errors</div>
              ) : (
                <>
                  Looks Good <CheckIcon className={classes.checkIcon} />
                </>
              )}
            </div>
          )}
          <Button link className={classes.refresh} onClick={handleRefreshAuditClick} icon={() => <HistoryIcon />}>
            Refresh
          </Button>
        </div>
      </div>
      <div className={classes.table}>
        <ProfileCoverageTable
          cols={columns}
          data={sitesList}
          loading={loadingSites}
          searchText={searchText}
          onRowClick={handleRowClick}
          rowActionsWidth={120}
          sticky
          stickyOffset={0}
          scrollContainer={scrollContainer?.current}
          rowActions={[
            <VRowAction key="details" label="View Profile" onClick={(row: ProfileSite) => () => handleRowClick(row)} />,
          ]}
        />
      </div>
    </div>
  );
};

export default BillingManagerList;
